/**
 * ScanSchedulePage - planification automatique des scans (Business+).
 *
 * v2026.06.144 : ajout fréquence 'daily'
 * v2026.06.145 : cases à cocher Scan M365 / Scan Azure
 * v2026.06.146 : bandeau "Prochain scan" + tableau historique 30j
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Card, CardHeader, Title3, Body1, Caption1, Button, Switch, Spinner,
  MessageBar, MessageBarBody, Dropdown, Option, Field, Radio, RadioGroup,
  Badge, Checkbox, Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  tokens,
} from '@fluentui/react-components';
import { Calendar24Regular, Sparkle24Filled, CloudArrowUp20Regular, History20Regular } from '@fluentui/react-icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader';
import { useLicense } from '../auth/LicenseGate';
import { useAuth } from '../auth/AuthContext';
import {
  scanScheduleApi,
  type ScanSchedule,
  type ScanScheduleFrequency,
  type ScanScheduleScopeMode,
  type ScanScheduleHistoryItem,
} from '../api/scanScheduleApi';
import { SCAN_SCOPE_PRODUCTS, type ScanScopeProduct } from '../api/scanScopeApi';
import { azureConnectionsApi } from '../api/azureConnectionsApi';

const BUSINESS_OR_HIGHER = new Set(['pro', 'business', 'enterprise']);

// --- Heure belge (Europe/Brussels). Le backend stocke hourUtc ; on affiche et
// laisse choisir en heure belge (CET/CEST), avec conversion UTC sous le capot.
function brusselsOffset(): number {
  const now = new Date();
  const utc = now.getUTCHours();
  const be  = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hour12: false, timeZone: 'Europe/Brussels' }).format(now));
  let d = be - utc;
  if (d > 12) d -= 24;
  if (d < -12) d += 24;
  return d; // +1 (hiver) ou +2 (été)
}
const BE_OFFSET = brusselsOffset();
const utcToBe = (h: number) => ((h + BE_OFFSET) % 24 + 24) % 24;
const beToUtc = (h: number) => ((h - BE_OFFSET) % 24 + 24) % 24;
function fmtBeFromUtc(h: number): string {
  const be = utcToBe(h);
  return `${String(be).padStart(2, '0')}:00 (Belgique) · ${String(h).padStart(2, '0')}:00 UTC`;
}

const PRODUCT_LABEL: Record<ScanScopeProduct, string> = {
  intune:        'Microsoft Intune',
  exchange:      'Exchange Online',
  purview:       'Microsoft Purview',
  teams:         'Microsoft Teams',
  powerPlatform: 'Power Platform',
  defenderXdr:   'Defender XDR',
  mcas:          'Defender for Cloud Apps',
};

export function ScanSchedulePage() {
  const { t } = useTranslation();
  const license = useLicense();
  const { user } = useAuth();

  const currentPlan = (license.status?.plan ?? '').toLowerCase();
  const hasProPlan  = Boolean(license.hasLicense && license.status?.valid && BUSINESS_OR_HIGHER.has(currentPlan));
  const canEdit     = hasProPlan && (user?.role === 'admin' || user?.role === 'auditor');

  return (
    <div>
      <PageHeader title={t('schedule.title')} description={t('schedule.subtitle')} />
      {!hasProPlan && <ProUpsellBanner currentPlan={currentPlan} />}
      <div style={{ marginTop: 16 }}>
        <ScheduleSection hasProPlan={hasProPlan} canEdit={canEdit} />
      </div>
      {hasProPlan && (
        <div style={{ marginTop: 24 }}>
          <HistorySection />
        </div>
      )}
    </div>
  );
}

function ScheduleSection({ hasProPlan, canEdit }: { hasProPlan: boolean; canEdit: boolean }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [saved, setSaved]     = useState(false);
  const [sched, setSched]     = useState<ScanSchedule | null>(null);
  const [initial, setInitial] = useState<ScanSchedule | null>(null);

  // Detection des connexions Azure (pour activer/desactiver la case Scan Azure)
  const azureQ = useQuery({
    queryKey: ['azure-connections-for-schedule'],
    queryFn:  () => azureConnectionsApi.list(),
    staleTime: 60_000,
    retry: false,
  });
  const hasAzureConnection = (azureQ.data?.connections?.length ?? 0) > 0;

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await scanScheduleApi.get();
      const norm: ScanSchedule = {
        enabled:             !!data.enabled,
        frequency:           (data.frequency ?? 'weekly') as ScanScheduleFrequency,
        dayOfWeek:           data.dayOfWeek ?? 1,
        dayOfMonth:          data.dayOfMonth ?? 1,
        hourUtc:             data.hourUtc ?? 6,
        scopeMode:           (data.scopeMode ?? 'inherit') as ScanScheduleScopeMode,
        excludedProducts:    data.excludedProducts ?? [],
        scanTypes:           data.scanTypes ?? { m365: true, azure: false },
        lastScheduledScanAt: data.lastScheduledScanAt ?? null,
        lastScheduledScanId: data.lastScheduledScanId ?? null,
        nextRunAt:           data.nextRunAt ?? null,
        planAllowed:         data.planAllowed,
        currentPlan:         data.currentPlan,
        updatedAt:           data.updatedAt,
        updatedBy:           data.updatedBy,
      };
      setSched(norm);
      setInitial(JSON.parse(JSON.stringify(norm)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const dirty = useMemo(() => {
    if (!sched || !initial) return false;
    return JSON.stringify({
      enabled: sched.enabled, frequency: sched.frequency, dayOfWeek: sched.dayOfWeek,
      dayOfMonth: sched.dayOfMonth, hourUtc: sched.hourUtc, scopeMode: sched.scopeMode,
      excludedProducts: [...sched.excludedProducts].sort(),
      scanTypes: sched.scanTypes,
    }) !== JSON.stringify({
      enabled: initial.enabled, frequency: initial.frequency, dayOfWeek: initial.dayOfWeek,
      dayOfMonth: initial.dayOfMonth, hourUtc: initial.hourUtc, scopeMode: initial.scopeMode,
      excludedProducts: [...initial.excludedProducts].sort(),
      scanTypes: initial.scanTypes,
    });
  }, [sched, initial]);

  // Validation : au moins un type de scan doit etre coche
  const scanTypesValid = useMemo(() => {
    if (!sched) return false;
    return !!(sched.scanTypes.m365 || sched.scanTypes.azure);
  }, [sched]);

  async function onSave() {
    if (!sched) return;
    if (!scanTypesValid) {
      setError(t('schedule.scanTypes.errorNoneSelected', 'Sélectionnez au moins un type de scan (M365 ou Azure).'));
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await scanScheduleApi.update({
        enabled:          sched.enabled,
        frequency:        sched.frequency,
        dayOfWeek:        sched.dayOfWeek,
        dayOfMonth:       sched.dayOfMonth,
        hourUtc:          sched.hourUtc,
        scopeMode:        sched.scopeMode,
        excludedProducts: sched.excludedProducts,
        scanTypes:        sched.scanTypes,
      });
      setInitial({ ...sched, updatedAt: res.updatedAt, updatedBy: res.updatedBy, nextRunAt: res.nextRunAt });
      setSched({ ...sched, updatedAt: res.updatedAt, updatedBy: res.updatedBy, nextRunAt: res.nextRunAt });
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function toggleExcluded(code: ScanScopeProduct) {
    if (!sched) return;
    const next = sched.excludedProducts.includes(code)
      ? sched.excludedProducts.filter((p) => p !== code)
      : [...sched.excludedProducts, code];
    setSched({ ...sched, excludedProducts: next });
  }

  if (loading) {
    return <Card style={{ padding: 24, textAlign: 'center' }}><Spinner size="small" /></Card>;
  }
  if (!sched) {
    return (
      <Card>
        <MessageBar intent="error"><MessageBarBody>{error ?? 'Schedule introuvable'}</MessageBarBody></MessageBar>
      </Card>
    );
  }

  const dayOfWeekLabels = [
    t('schedule.day.sunday'),
    t('schedule.day.monday'),
    t('schedule.day.tuesday'),
    t('schedule.day.wednesday'),
    t('schedule.day.thursday'),
    t('schedule.day.friday'),
    t('schedule.day.saturday'),
  ];

  return (
    <Card style={{ opacity: hasProPlan ? 1 : 0.55 }}>
      <CardHeader
        image={<Calendar24Regular primaryFill={tokens.colorBrandForeground1} />}
        header={<Title3>{t('schedule.section.title')}</Title3>}
      />

      {error   && <MessageBar intent="error"   style={{ marginBottom: 12 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {saved   && <MessageBar intent="success" style={{ marginBottom: 12 }}><MessageBarBody>{t('schedule.saved')}</MessageBarBody></MessageBar>}
      {!canEdit && <MessageBar intent="info"   style={{ marginBottom: 12 }}><MessageBarBody>{t('schedule.readOnlyHint')}</MessageBarBody></MessageBar>}

      {/* Toggle enabled */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, marginBottom: 16,
      }}>
        <div style={{ flex: 1 }}>
          <Body1 style={{ fontWeight: 600, display: 'block' }}>{t('schedule.enabled.title')}</Body1>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
            {t('schedule.enabled.hint')}
          </Caption1>
        </div>
        <Switch
          checked={sched.enabled}
          disabled={!canEdit || saving}
          onChange={(_, d) => setSched({ ...sched, enabled: d.checked })}
        />
      </div>

      {/* Types de scan (M365 / Azure) - v2026.06.145 */}
      <Field
        label={t('schedule.scanTypes.label', 'Types de scan')}
        hint={t('schedule.scanTypes.hint', 'Cochez les scans à lancer à chaque déclenchement.')}
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Checkbox
            checked={sched.scanTypes.m365}
            disabled={!canEdit || !sched.enabled || saving}
            onChange={(_, d) => setSched({ ...sched, scanTypes: { ...sched.scanTypes, m365: !!d.checked } })}
            label={t('schedule.scanTypes.m365', 'Scan M365 (Entra ID, Exchange, Purview, Intune…)')}
          />
          <Checkbox
            checked={sched.scanTypes.azure && hasAzureConnection}
            disabled={!canEdit || !sched.enabled || saving || !hasAzureConnection}
            onChange={(_, d) => setSched({ ...sched, scanTypes: { ...sched.scanTypes, azure: !!d.checked } })}
            label={
              <span>
                {t('schedule.scanTypes.azure', 'Scan Azure (subscriptions connectées)')}
                {!hasAzureConnection && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, marginLeft: 8 }}>
                    — {t('schedule.scanTypes.azureUnavailable', 'aucune connexion Azure configurée')}
                    {' '}<Link to="/tenants" style={{ color: tokens.colorBrandForeground1 }}>
                      {t('schedule.scanTypes.azureConnect', 'Connecter')}
                    </Link>
                  </Caption1>
                )}
              </span>
            }
          />
        </div>
      </Field>

      {/* Fréquence */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
        <Field label={t('schedule.frequency.label')} hint={t('schedule.frequency.hint')}>
          <Dropdown
            value={t(`schedule.frequency.${sched.frequency}`)}
            selectedOptions={[sched.frequency]}
            disabled={!canEdit || !sched.enabled || saving}
            onOptionSelect={(_, d) => setSched({ ...sched, frequency: (d.optionValue as ScanScheduleFrequency) ?? 'weekly' })}
          >
            <Option value="daily">{t('schedule.frequency.daily', 'Quotidien')}</Option>
            <Option value="weekly">{t('schedule.frequency.weekly')}</Option>
            <Option value="monthly">{t('schedule.frequency.monthly')}</Option>
          </Dropdown>
        </Field>

        {sched.frequency === 'weekly' && (
          <Field label={t('schedule.dayOfWeek.label')} hint={t('schedule.dayOfWeek.hint')}>
            <Dropdown
              value={dayOfWeekLabels[sched.dayOfWeek] ?? ''}
              selectedOptions={[String(sched.dayOfWeek)]}
              disabled={!canEdit || !sched.enabled || saving}
              onOptionSelect={(_, d) => setSched({ ...sched, dayOfWeek: Number(d.optionValue ?? 1) })}
            >
              {dayOfWeekLabels.map((label, i) => (
                <Option key={i} value={String(i)} text={label}>{label}</Option>
              ))}
            </Dropdown>
          </Field>
        )}

        {sched.frequency === 'monthly' && (
          <Field label={t('schedule.dayOfMonth.label')} hint={t('schedule.dayOfMonth.hint')}>
            <Dropdown
              value={String(sched.dayOfMonth)}
              selectedOptions={[String(sched.dayOfMonth)]}
              disabled={!canEdit || !sched.enabled || saving}
              onOptionSelect={(_, d) => setSched({ ...sched, dayOfMonth: Number(d.optionValue ?? 1) })}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <Option key={day} value={String(day)} text={String(day)}>{String(day)}</Option>
              ))}
            </Dropdown>
          </Field>
        )}

        <Field label={t('schedule.hour.label')} hint={t('schedule.hour.hintBe', 'Heure de déclenchement (fuseau Europe/Bruxelles).')}>
          <Dropdown
            value={fmtBeFromUtc(sched.hourUtc)}
            selectedOptions={[String(sched.hourUtc)]}
            disabled={!canEdit || !sched.enabled || saving}
            onOptionSelect={(_, d) => setSched({ ...sched, hourUtc: Number(d.optionValue ?? 6) })}
          >
            {Array.from({ length: 24 }, (_, i) => i).map((beH) => {
              const utcH = beToUtc(beH);
              const label = `${String(beH).padStart(2, '0')}:00 (Belgique) · ${String(utcH).padStart(2, '0')}:00 UTC`;
              return <Option key={beH} value={String(utcH)} text={label}>{label}</Option>;
            })}
          </Dropdown>
        </Field>
      </div>

      {/* Scope */}
      <Field label={t('schedule.scope.label')} hint={t('schedule.scope.hint')} style={{ marginBottom: 16 }}>
        <RadioGroup
          value={sched.scopeMode}
          disabled={!canEdit || !sched.enabled || saving}
          onChange={(_, d) => setSched({ ...sched, scopeMode: (d.value as ScanScheduleScopeMode) })}
        >
          <Radio value="inherit" label={t('schedule.scope.inherit')} />
          <Radio value="custom"  label={t('schedule.scope.custom')} />
        </RadioGroup>
      </Field>

      {sched.scopeMode === 'custom' && (
        <div style={{ marginBottom: 16, paddingLeft: 24 }}>
          <Caption1 style={{ display: 'block', marginBottom: 8, color: tokens.colorNeutralForeground3 }}>
            {t('schedule.scope.customHint')}
          </Caption1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SCAN_SCOPE_PRODUCTS.map((code) => {
              const isExcluded = sched.excludedProducts.includes(code);
              return (
                <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Switch checked={!isExcluded} disabled={!canEdit || !sched.enabled || saving} onChange={() => toggleExcluded(code)} />
                  <Body1>{PRODUCT_LABEL[code]}</Body1>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bandeau Prochain scan - v2026.06.146 */}
      {sched.enabled && sched.nextRunAt && (
        <div style={{
          background: tokens.colorBrandBackground2,
          border: `1px solid ${tokens.colorBrandStroke2}`,
          borderRadius: 8, padding: '12px 16px', marginTop: 16, marginBottom: 8,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <CloudArrowUp20Regular primaryFill={tokens.colorBrandForeground1} />
          <div style={{ flex: 1 }}>
            <Body1 style={{ fontWeight: 600, color: tokens.colorBrandForeground1, display: 'block' }}>
              {t('schedule.nextRun.title', 'Prochain scan planifié')}
            </Body1>
            <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground2 }}>
              {new Date(sched.nextRunAt).toLocaleString('fr-BE', { timeZone: 'Europe/Brussels', dateStyle: 'long', timeStyle: 'short' })} (heure belge)
            </Caption1>
          </div>
        </div>
      )}

      {/* Footer summary + save */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap', marginTop: 16, paddingTop: 16,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
      }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {sched.lastScheduledScanAt
            ? t('schedule.lastRun', { date: new Date(sched.lastScheduledScanAt).toLocaleString() })
            : t('schedule.lastRunNever')}
          {sched.updatedAt && (
            <span style={{ marginLeft: 12 }}>
              · {t('schedule.lastUpdated', {
                date: new Date(sched.updatedAt).toLocaleDateString(),
                user: sched.updatedBy ?? '',
              })}
            </span>
          )}
        </Caption1>
        {canEdit && (
          <Button
            appearance="primary"
            onClick={onSave}
            disabled={!dirty || saving || !scanTypesValid}
            icon={saving ? <Spinner size="tiny" /> : undefined}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// v2026.06.146 - Historique des scans (M365 + Azure, 30 derniers jours)
// ---------------------------------------------------------------------------
function HistorySection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['scan-schedule-history', 30],
    queryFn:  () => scanScheduleApi.history(30),
  });

  const items: ScanScheduleHistoryItem[] = data?.items ?? [];

  return (
    <Card>
      <CardHeader
        image={<History20Regular primaryFill={tokens.colorBrandForeground1} />}
        header={<Title3>{t('schedule.history.title', 'Historique des scans (30 derniers jours)')}</Title3>}
        description={
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            {t('schedule.history.subtitle', 'Tous les scans, manuels et planifiés, M365 et Azure.')}
          </Caption1>
        }
      />

      {isLoading && <div style={{ padding: 24, textAlign: 'center' }}><Spinner size="small" /></div>}
      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>{(error as Error).message}</MessageBarBody>
        </MessageBar>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
          <Caption1>{t('schedule.history.empty', 'Aucun scan dans les 30 derniers jours.')}</Caption1>
        </div>
      )}

      {items.length > 0 && (
        <Table style={{ marginTop: 12 }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: 170 }}>{t('schedule.history.col.date', 'Date')}</TableHeaderCell>
              <TableHeaderCell style={{ width: 100 }}>{t('schedule.history.col.type', 'Type')}</TableHeaderCell>
              <TableHeaderCell style={{ width: 110 }}>{t('schedule.history.col.source', 'Source')}</TableHeaderCell>
              <TableHeaderCell style={{ width: 110 }}>{t('schedule.history.col.status', 'Statut')}</TableHeaderCell>
              <TableHeaderCell style={{ width: 80 }}>{t('schedule.history.col.score', 'Score')}</TableHeaderCell>
              <TableHeaderCell>{t('schedule.history.col.label', 'Détail')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow
                key={it.id}
                onClick={() => navigate(it.detailHref)}
                style={{ cursor: 'pointer' }}
              >
                <TableCell style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                  {it.startedAt ? new Date(it.startedAt).toLocaleString() : '—'}
                </TableCell>
                <TableCell>
                  <Badge appearance="outline" color={it.type === 'azure' ? 'informative' : 'brand'}>
                    {it.type === 'azure' ? 'Azure' : 'M365'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="filled" color={it.source === 'scheduled' ? 'success' : 'subtle'}>
                    {it.source === 'scheduled'
                      ? t('schedule.history.source.scheduled', 'Auto')
                      : t('schedule.history.source.manual', 'Manuel')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <StatusBadge status={it.status} />
                </TableCell>
                <TableCell>
                  {it.score != null ? `${Math.round(it.score)}%` : '—'}
                </TableCell>
                <TableCell style={{ fontSize: 13 }}>{it.label}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = (status ?? '').toLowerCase();
  let color: 'success' | 'danger' | 'warning' | 'subtle' = 'subtle';
  if (s === 'success' || s === 'completed' || s === 'done') color = 'success';
  else if (s === 'failed' || s === 'error') color = 'danger';
  else if (s === 'running' || s === 'in-progress' || s === 'queued') color = 'warning';
  return <Badge appearance="filled" color={color}>{status || '—'}</Badge>;
}

function ProUpsellBanner({ currentPlan }: { currentPlan: string }) {
  const { t } = useTranslation();
  const currentLabel = currentPlan || t('billing.plan.trial');
  return (
    <Card style={{
      background: 'linear-gradient(135deg, #FFF3D6 0%, #FFE8B5 100%)',
      border: '1.5px solid #D9842B', padding: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <Sparkle24Filled style={{ color: '#B45309' }} />
        <div style={{ flex: 1 }}>
          <Title3 style={{ color: '#8A6308', margin: 0 }}>{t('schedule.upsell.title')}</Title3>
          <Caption1 style={{ color: '#8A6308', display: 'block', marginTop: 4 }}>
            {t('schedule.upsell.currentPlan', { plan: currentLabel })}
          </Caption1>
        </div>
        <Link
          to="/billing"
          style={{
            background: '#1F3D5C', color: '#fff', padding: '10px 18px',
            borderRadius: 8, fontWeight: 700, fontSize: 14,
            textDecoration: 'none', whiteSpace: 'nowrap',
          }}
        >
          {t('schedule.upsell.cta')}
        </Link>
      </div>
      <MessageBar intent="info" style={{ background: 'rgba(255,255,255,.6)' }}>
        <MessageBarBody style={{ fontSize: 13 }}>{t('schedule.upsell.perks')}</MessageBarBody>
      </MessageBar>
    </Card>
  );
}
